import { randomBytes } from 'crypto';
import { createContext, useState } from 'react';
import {
  SystemMessage,
  SystemMessageType,
} from '../../libs/system/SystemMessage';

export type SystemMessageContextType = {
  readonly messages: SystemMessage[];
  removeMessage: (systemMessageId: string) => Promise<void>;
  success: (body: string) => Promise<SystemMessage>;
  info: (body: string) => Promise<SystemMessage>;
  warn: (body: string) => Promise<SystemMessage>;
  danger: (body: string) => Promise<SystemMessage>;
};

// MEMO: 初期値は仮実装で埋める
export const SystemMessageContext = createContext<SystemMessageContextType>({
  messages: [],
  removeMessage: async (): Promise<void> => {
    throw new Error('Not implemented');
  },
  success: (): Promise<SystemMessage> => {
    throw new Error('Not implemented');
  },
  info: (): Promise<SystemMessage> => {
    throw new Error('Not implemented');
  },
  warn: (): Promise<SystemMessage> => {
    throw new Error('Not implemented');
  },
  danger: (): Promise<SystemMessage> => {
    throw new Error('Not implemented');
  },
});

/**
 * システムメッセージのIDを作成
 */
const createSystemMessageId = (): string => {
  return `${new Date().getTime()}_${randomBytes(64).toString('hex')}`;
};

/**
 * システムメッセージのエンティティを作成
 */
const createSystemMessageEntity = (
  type: SystemMessageType,
  body: string
): SystemMessage => {
  return {
    id: createSystemMessageId(),
    type,
    body,
  };
};

export const useSystemMessageContext = (): SystemMessageContextType => {
  const [state, setState] = useState({
    messages: [] as SystemMessage[],
  });

  /**
   * メッセージ削除
   */
  const removeMessage = async (systemMessageId: string): Promise<void> => {
    const newMessages = state.messages.filter(
      (mes) => mes.id !== systemMessageId
    );
    setState({
      messages: newMessages,
    });
  };

  /**
   * メッセージ追加
   */
  const addMessage = async (
    type: SystemMessageType,
    body: string
  ): Promise<SystemMessage> => {
    const systemMessage = createSystemMessageEntity(type, body);
    state.messages.push(systemMessage);
    setState({
      messages: state.messages,
    });
    return Promise.resolve(systemMessage);
  };

  return {
    get messages() {
      return state.messages;
    },
    removeMessage,
    async success(body: string): Promise<SystemMessage> {
      return addMessage('success', body);
    },
    async info(body: string): Promise<SystemMessage> {
      return addMessage('info', body);
    },
    async warn(body: string): Promise<SystemMessage> {
      return addMessage('warn', body);
    },
    async danger(body: string): Promise<SystemMessage> {
      return addMessage('danger', body);
    },
  };
};
